import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import logoFigure from "@/assets/logo-arzani-white.webp";
import heroImage from "@/assets/dest-saint-tropez-hero.webp";

import stTropezDestination from "@/assets/saint-tropez-destination.webp";
import yachtImage from "@/assets/st-tropez-yachts.jpg";
import driveImage from "@/assets/st-tropez-drive.jpg";
import lifestyleHero from "@/assets/st-tropez-lifestyle.jpg";
import residencesHero from "@/assets/st-tropez-residences.webp";
import villaInterior from "@/assets/villa-interior.webp";
import yachtPool from "@/assets/st-tropez-yacht-pool.webp";

import lifestyleRestaurant from "@/assets/lifestyle-restaurant.webp";
import stTropezRhythm from "@/assets/st-tropez-rhythm.webp";
import lifestyleDining from "@/assets/lifestyle-dining-ibiza.webp";
import stTropezLanterns from "@/assets/st-tropez-lanterns.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const services = [
  {
    title: "Yachts",
    description: "Private day charters along the Côte d'Azur, from the Gulf of Saint-Tropez to the Îles d'Hyères and beyond.",
    image: yachtImage,
    path: null,
  },
  {
    title: "Drive",
    description: "Prestige vehicles delivered to your door — from elegant convertibles to high-performance sports cars along the Riviera.",
    image: driveImage,
    path: null,
  },
  {
    title: "Lifestyle",
    description: "Beach club reservations, private dining, wellness, personal chefs, and bespoke Riviera experiences arranged with care.",
    image: lifestyleHero,
    path: "/saint-tropez/lifestyle",
  },
  {
    title: "Residences",
    description: "Exceptional villas and apartments across Ramatuelle, Pampelonne, and the hills above Saint-Tropez.",
    image: residencesHero,
    path: "/saint-tropez/residences",
  },
];

const SaintTropez = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Group Saint-Tropez — Riviera Residences & Lifestyle"
        description="Private villas, beach club access, chauffeur and bespoke lifestyle across Saint-Tropez and the Côte d'Azur."
        path="/saint-tropez"
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", serviceType: "Luxury concierge", areaServed: "Saint-Tropez, France", provider: { "@type": "Organization", name: "Arzani Group" } }}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video 
            src="/videos/st-tropez-hero.mov" 
            autoPlay 
            muted 
            loop 
            playsInline 
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/20 to-foreground/60" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-background" />
        </div>
        <div className="relative z-10 text-center px-8 flex flex-col items-center">
          <motion.img
            src={logoFigure}
            alt="Arzani Group"
            className="h-20 md:h-28 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.8, ease }}
          />
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.8, ease }}
          >
            Arzani Group
          </motion.h1>
          <motion.p
            className="font-label text-primary-foreground/60 tracking-[0.4em] mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1.8, ease }}
          >
            S A I N T - T R O P E Z
          </motion.p>
          <motion.p
            className="font-accent text-primary-foreground/60 text-sm md:text-base italic max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1.8, ease }}
          >
            Riviera glamour,<br />effortlessly orchestrated.
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1.5, ease }}
        >
          <span className="font-label text-primary-foreground/30 text-[8px]">Scroll</span>
          <div className="w-px h-8 bg-primary-foreground/20" />
        </motion.div>
      </section>

      {/* Editorial Intro — Asymmetric */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={stTropezDestination}
                alt="Mediterranean sea"
                className="w-full h-[280px] md:h-[420px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                The Destination
              </span>
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Saint-Tropez is where Riviera elegance meets a timeless way of living.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                Through Arzani, every detail of your stay is arranged in advance — from coastal cruising
                and private residences to refined dining and seamless access across the peninsula.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Centered Statement */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.p
            className="font-accent text-foreground/60 text-base md:text-lg lg:text-xl leading-[1.8] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            Set along the Côte d'Azur, Saint-Tropez offers a balance between understated luxury
            and vibrant coastal energy. Days unfold between Pampelonne Beach and open sea,
            while evenings transition into long dinners, quiet streets, and refined nightlife around the port.
            <br /><br />
            With Arzani, each element is aligned before you arrive — allowing your experience
            to move naturally, without interruption.
          </motion.p>
        </div>
      </section>

      {/* Services — Editorial Grid */}
      <section id="services" className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
              Our Services
            </span>
            <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase mb-4">
              Curated Services in Saint-Tropez
            </h2>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-xl">
              Seamless access to the Riviera's most sought-after experiences, thoughtfully arranged around your stay.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                className="group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.2, delay: i * 0.08, ease }}
              >
                <div className="overflow-hidden mb-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[220px] md:h-[280px] object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-display text-lg md:text-xl text-foreground tracking-[0.12em] uppercase mb-3">
                  Arzani {service.title}
                </h3>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-sm mb-5">
                  {service.description}
                </p>
                {service.path ? (
                  <Link
                    to={service.path}
                    className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                  >
                    Explore
                  </Link>
                ) : (
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                  >
                    Inquire
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="bg-background py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row">
            {[
              {
                title: "Submit Your Request",
                text: "Tell us your plans, preferences, and dates in Saint-Tropez.",
              },
              {
                title: "Curated Options",
                text: "We present a tailored selection of yachts, vehicles, residences, and experiences.",
              },
              {
                title: "Confirm & Arrange",
                text: "Once confirmed, every detail is secured and prepared in advance.",
              },
              {
                title: "Experience the Riviera",
                text: "Arrive and enjoy — everything is already in place.",
              },
            ].map((item, i, arr) => (
              <motion.div
                key={item.title}
                className="flex md:flex-col flex-1 group"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease }}
              >
                <div className="flex flex-col md:flex-row items-center mr-5 md:mr-0 md:mb-6">
                  <div className="w-5 h-5 rounded-full border border-foreground/20 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-px md:w-full h-full md:h-px bg-foreground/10 md:flex-1 min-h-[40px] md:min-h-0 ml-0 md:ml-0" />
                  )}
                  {i === arr.length - 1 && <div className="md:flex-1" />}
                </div>
                <div className="pb-8 md:pb-0 md:pr-8">
                  <span className="font-label text-[10px] text-foreground/25 tracking-[0.2em] block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-sm text-foreground tracking-[0.15em] uppercase mb-1.5">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-xs leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Images + Text */}
      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <motion.div
              className="lg:col-span-4 flex flex-col gap-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <div>
                <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                  Saint-Tropez is defined by rhythm.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] mb-5">
                  Mornings begin quietly.<br />
                  Afternoons shift toward the coastline.<br />
                  Evenings settle into long, unhurried dinners.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] mb-5">
                  Through Arzani Lifestyle, each moment is arranged in advance — allowing you to move
                  between beach, residence, and dining without effort.
                </p>
                <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                  Movement across Saint-Tropez is essential.
                </p>
              </div>
              <img
                src={yachtPool}
                alt="Yacht on the Côte d'Azur"
                className="w-full h-[160px] md:h-[200px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-7 lg:col-start-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.1, ease }}
            >
              <img
                src={stTropezRhythm}
                alt="Saint-Tropez beach club atmosphere"
                className="w-full h-[280px] md:h-[400px] object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Block */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={stTropezLanterns}
                alt="Saint-Tropez evening ambiance"
                className="w-full h-[180px] md:h-[240px] object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="lg:col-span-7 lg:col-start-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, delay: 0.1, ease }}
            >
              <p className="font-accent text-primary-foreground/60 text-sm md:text-base lg:text-lg leading-[1.8] italic">
                Our role is to understand how each element connects — so your time
                on the Riviera moves with the effortless rhythm you expect.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-background">
        <div className="container-luxury">

          <motion.div
            className="max-w-2xl mx-auto text-center mt-14 md:mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            <p className="font-body text-foreground/60 text-xs md:text-sm leading-[2] mb-5">
              From beach clubs to Michelin restaurants, from sunrise wellness to
              late-night dining — every reservation, every transfer, every detail
              is handled before you arrive.
            </p>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[2]">
              No waiting. No uncertainty. Just access.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA — Get in Touch */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.span
            className="font-label text-bronze block mb-6 text-[10px] tracking-[0.3em]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Coming Soon
          </motion.span>
          <motion.h2
            className="font-display text-2xl md:text-4xl text-primary-foreground tracking-[0.12em] uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Saint-Tropez, Summer 2026
          </motion.h2>
          <motion.p
            className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[2] mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 1.2, ease }}
          >
            We are preparing our full Saint-Tropez offering. Register your interest
            and be the first to access our curated services on the Côte d'Azur.
          </motion.p>
          <motion.a
            href="https://wa.me/message/2XEKJ5STR2IZJ1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-3 border border-primary-foreground/30 text-primary-foreground/80 text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 1, ease }}
          >
            Get in touch
          </motion.a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SaintTropez;
